import React, { useCallback, useEffect } from 'react'

import { getAllExternalFlightBookingRequestsForAdmin } from '@/redux/states/admin/trips/getAllExternalFlightBookingRequestsForAdmin/getAllExternalFlightBookingRequestsForAdmin.slice'
import { GetAllExternalFlightBookingRequestsForAdminRequestPayload } from '@/redux/states/admin/trips/getAllExternalFlightBookingRequestsForAdmin/getAllExternalFlightBookingRequestsForAdmin.types'
import { getCountriesAndCities } from '@/redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { getExternalAirlines } from '@/redux/states/flights/getExternalAirlines/getExternalAirlines.slice'
import status from '@/shared/functions/Status/status.dictionaries'
import { User } from '@/shared/types/users.types'
import { Chip, Stack, Typography } from '@mui/material'
import { AppProvider, DashboardLayout, PageContainer } from '@toolpad/core'
import { useDemoRouter } from '@toolpad/core/internal'
import { useSelector } from 'react-redux'

import DiscountCodes from './components/DiscountCodes/DiscountCodes.component'
import ExternalBookings from './components/ExternalBookings/ExternalBookings.component'
import FulfillmentOrderDetails from './components/FulfillmentOrders/FulfillmentOrderDetails.component'
import FulfillmentOrders from './components/FulfillmentOrders/FulfillmentOrders.component'
import LockerOrders from './components/LockerOrders/LockerOrders.component'
import Lockers from './components/Lockers/Lockers.component'
import OfferRequests from './components/OfferRequests/OfferRequests.component'
import ReadyToPurchase from './components/ReadyToPurchase/ReadyToPurchase.component'
import SearchHeader from './components/SearchHeader/SearchHeader.component'
import Trips from './components/Trips/Trips.component'
import Users from './components/Users/Users.component'
import UserDetails from './components/Users/components/UserDetails/UserDetails.component'
import OrderDetails from './components/Users/components/UserDetails/components/OrderDetails/OrderDetails.component'
import TripDetails from './components/Users/components/UserDetails/components/TripDetails/TripDetails.component'

import images from '../../assets/images'
import env from '../../networkRequests/apiClient/env.config'
import { logout } from '../../redux/states/auth/logout/logout.slice'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import routes from '../../router/routes.dictionary'
import { stripPrefixFromId } from '../../shared/functions/ID/id.functions'
import { joinElementsWithBullet } from '../../shared/functions/String/string.functions'
import { UserRole } from '../../shared/functions/UserRole/userRoleManagement.types'
import { setUserRole } from '../../shared/functions/UserRole/userRoleManagment.functions'
import { useAppDispatch } from '../../shared/hooks/redux.hooks'
import linkStyles from '../../shared/styles/link.styles'
import theme from '../../shared/styles/themes/default.theme'
import { adminNavigation, adminNavigationPaths } from './AdminDashboard.dictionary'
import styles from './AdminDashboard.styles'
import {
  AdminNavigationItem,
  AdminNavigationPath,
  AdminPageContentHeaderText,
  Breadcrumb
} from './AdminDashboard.types'

const AdminDashboard: React.FC = () => {
  const [userSearch, setUserSearch] = React.useState('')
  const [orderDetailsSearch, setOrderDetailsSearch] = React.useState('')
  const [tripDetailsSearch, setTripDetailsSearch] = React.useState('')
  const [discountCodesSearch, setDiscountCodesSearch] = React.useState('')
  const [readyToPurchaseSearch, setReadyToPurchaseSearch] = React.useState('')
  const [lockerOrdersSearch, setLockerOrdersSearch] = React.useState('')
  const [externalBookingsSearch, setExternalBookingsSearch] = React.useState('')
  const [offerRequestsSearch, setOfferRequestsSearch] = React.useState('')
  const [fulfillmentOrdersSearch, setFulfillmentOrdersSearch] = React.useState('')
  const [tripsSearch, setTripsSearch] = React.useState('')
  const [userDetailsTabIndex, setUserDetailsTabIndex] = React.useState(-1)
  const [discountCodesTabIndex, setDiscountCodesTabIndex] = React.useState(0)
  const [externalBookingsTabIndex, setExternalBookingsTabIndex] = React.useState(0)
  const [readyToPurchaseTabIndex, setReadyToPurchaseTabIndex] = React.useState(0)
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null)
  const [adminNavigationPath, setAdminNavigationPath] = React.useState<AdminNavigationItem[]>(adminNavigation)

  const { success: usersSuccess } = useSelector((state: RootState) => state.getAllUsersForAdmin)

  const { success: fulfillmentOrderDetailsSuccess } = useSelector(
    (state: RootState) => state.getFulfillmentOrderDetailsForAdmin
  )

  const { success: externalBookingRequestsSuccess } = useSelector(
    (state: RootState) => state.getAllExternalFlightBookingRequestsForAdmin
  )

  const dispatch = useAppDispatch()
  const navigate = router.navigate()
  const demoRouter = useDemoRouter('users')

  const loadHomePageEndpoints = useCallback(
    async (pathname: string) => {
      const promises: Promise<unknown>[] = []

      const externalBookingRequestsPayload: GetAllExternalFlightBookingRequestsForAdminRequestPayload = {
        request: {
          status: status.externalFlightBookingStatus.externalFlightBookingRequestCreated
        }
      }

      switch (pathname) {
        case `/${adminNavigationPaths.users}`:
          promises.push(dispatch(getAllExternalFlightBookingRequestsForAdmin(externalBookingRequestsPayload)))
          break

        case `/${adminNavigationPaths.externalBookings}`:
          break
      }

      await Promise.all(promises)
    },
    [dispatch]
  )

  const loadExternalBookingsAuxiliaryEndpoints = useCallback(async () => {
    dispatch(getCountriesAndCities({}))
    dispatch(getExternalAirlines({}))
  }, [dispatch])

  useEffect(() => {
    setUserRole(UserRole.admin)
    loadHomePageEndpoints(adminNavigationPaths.users)
    loadExternalBookingsAuxiliaryEndpoints()
  }, [loadExternalBookingsAuxiliaryEndpoints, loadHomePageEndpoints])

  useEffect(
    function loadHomePageEndpointsOnPathChange() {
      loadHomePageEndpoints(demoRouter.pathname)
    },
    [demoRouter.pathname, loadHomePageEndpoints]
  )

  useEffect(() => {
    const externalBookingsItem = adminNavigationPath.find(
      (item) => item.segment === adminNavigationPaths.externalBookings
    )

    const records = externalBookingRequestsSuccess?.data?.records ?? []
    const newExternalBookingRequestsCount = records.filter(
      (record) => record.status.name === status.externalFlightBookingStatus.externalFlightBookingRequestCreated
    ).length

    if (externalBookingsItem && newExternalBookingRequestsCount > 0) {
      externalBookingsItem.action = <Chip size="small" label={`${newExternalBookingRequestsCount} new`} color="info" />

      const navPath = adminNavigationPath.map((item) => {
        if (item.segment === adminNavigationPaths.externalBookings) {
          return { ...item, action: externalBookingsItem.action }
        }

        return item
      })

      setAdminNavigationPath(navPath)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalBookingRequestsSuccess])

  const getCurrentAdminPath = useCallback(() => {
    const pathSegments = demoRouter.pathname.split('/').filter(Boolean)
    const mainSection = pathSegments[0] as AdminNavigationPath
    const userId = pathSegments[1]
    const isOnUserDetailsPath = pathSegments[1] !== undefined

    const isOnOrderDetailsPathFromUserDetails = pathSegments[2] !== undefined && userDetailsTabIndex === 0
    const isOnOrderDetailsPathFromOfferRequests =
      pathSegments[1] !== undefined && mainSection === adminNavigationPaths.offerRequests
    const isOnOrderDetailsPath = isOnOrderDetailsPathFromUserDetails || isOnOrderDetailsPathFromOfferRequests

    const isOnTripDetailsPathFromUserDetails = pathSegments[2] !== undefined && userDetailsTabIndex === 1
    const isOnTripDetailsPathFromTrips = pathSegments[1] !== undefined && mainSection === adminNavigationPaths.trips
    const isOnTripDetailsPath = isOnTripDetailsPathFromUserDetails || isOnTripDetailsPathFromTrips

    const isOnFulfillmentOrderDetailsPath =
      pathSegments[1] !== undefined && mainSection === adminNavigationPaths.fulfillmentOrders
    const fulfillmentOrderId = isOnFulfillmentOrderDetailsPath ? pathSegments[1] : undefined

    return {
      mainSection,
      isOnUserDetailsPath,
      isOnOrderDetailsPath,
      isOnTripDetailsPath,
      isOnFulfillmentOrderDetailsPath,
      fulfillmentOrderId,
      userId
    }
  }, [demoRouter.pathname, userDetailsTabIndex])

  const getOfferRequestId = (): number | null => {
    const pathSegments = demoRouter.pathname.split('/').filter(Boolean)
    const offerRequestIdIndex = pathSegments.findIndex((segment) => segment === 'Orders') + 1

    if (offerRequestIdIndex > 0 && pathSegments[offerRequestIdIndex]) {
      return parseInt(pathSegments[offerRequestIdIndex], 10)
    }

    return null
  }

  // Set when an order is opened from a link to one of its items, e.g. .../Orders/92/Items/701
  const getFocusedItemRequestId = (): number | null => {
    const pathSegments = demoRouter.pathname.split('/').filter(Boolean)
    const itemRequestIdIndex = pathSegments.findIndex((segment) => segment === 'Items') + 1

    if (itemRequestIdIndex > 0 && pathSegments[itemRequestIdIndex]) {
      return parseInt(pathSegments[itemRequestIdIndex], 10)
    }

    return null
  }

  const handleSearchSubmit = useCallback(
    (query: string) => {
      const { mainSection, isOnUserDetailsPath, isOnOrderDetailsPath, isOnTripDetailsPath } = getCurrentAdminPath()

      switch (mainSection) {
        case adminNavigationPaths.users:
          if (isOnUserDetailsPath) {
            if (isOnOrderDetailsPath) {
              setOrderDetailsSearch(query)
            } else if (isOnTripDetailsPath) {
              setTripDetailsSearch(query)
            }
          } else {
            setUserSearch(query)
          }
          break

        case adminNavigationPaths.discountCodes:
          setDiscountCodesSearch(query)
          break

        case adminNavigationPaths.externalBookings:
          setExternalBookingsSearch(query)
          break

        case adminNavigationPaths.offerRequests:
          setOfferRequestsSearch(query)
          break

        case adminNavigationPaths.fulfillmentOrders: {
          const { isOnFulfillmentOrderDetailsPath } = getCurrentAdminPath()

          if (!isOnFulfillmentOrderDetailsPath) {
            setFulfillmentOrdersSearch(query)
          }
          break
        }

        case adminNavigationPaths.trips:
          setTripsSearch(query)
          break

        case adminNavigationPaths.lockerOrders:
          setLockerOrdersSearch(query)
          break

        case adminNavigationPaths.readyToPurchase:
          setReadyToPurchaseSearch(query)
          break
      }
    },
    [getCurrentAdminPath]
  )

  const handleClearSearch = React.useCallback(() => {
    setUserSearch('')
    setOrderDetailsSearch('')
    setTripDetailsSearch('')
    setDiscountCodesSearch('')
    setExternalBookingsSearch('')
    setOfferRequestsSearch('')
    setFulfillmentOrdersSearch('')
    setTripsSearch('')
    setReadyToPurchaseSearch('')
    setLockerOrdersSearch('')
  }, [])

  function getBreadcrumbConfig(currentPath: string) {
    const basePath = routes.adminDashboard.path
    const pathSegments = currentPath.replace(basePath, '').split('/').filter(Boolean)
    let accumulatedPath = basePath

    const baseCrumb = {
      title: '🏠',
      path: basePath
    }

    const breadcrumbs: Breadcrumb[] = [baseCrumb]

    const pathDisplayNames: Record<string, string> = {
      [adminNavigationPaths.users]: 'Users',
      [adminNavigationPaths.externalBookings]: 'External Bookings',
      [adminNavigationPaths.discountCodes]: 'Discount Codes',
      [adminNavigationPaths.offerRequests]: 'Offer Requests',
      [adminNavigationPaths.fulfillmentOrders]: 'Fulfillment Orders',
      [adminNavigationPaths.readyToPurchase]: 'Ready to Purchase',
      [adminNavigationPaths.trips]: 'Trips',
      [adminNavigationPaths.lockers]: 'Lockers',
      [adminNavigationPaths.lockerOrders]: 'Locker Orders'
    }

    for (const [index, segment] of pathSegments.entries()) {
      const displayName = pathDisplayNames[segment] || stripPrefixFromId(segment)
      const isLast = index === pathSegments.length - 1
      accumulatedPath += `/${segment}`

      breadcrumbs.push({
        title: displayName,
        path: isLast ? '' : accumulatedPath
      })
    }

    // Truncate long IDs (like user IDs) in last breadcrumb
    if (breadcrumbs.length > 1) {
      const lastCrumb = breadcrumbs[breadcrumbs.length - 1]

      if (lastCrumb.title.length > 20) {
        lastCrumb.title = `${lastCrumb.title.substring(0, 8)}...${lastCrumb.title.substring(
          lastCrumb.title.length - 4
        )}`
      }

      lastCrumb.path = '' // Remove link from current page
    }

    return breadcrumbs
  }

  function getUserDetailsPageContent() {
    const { mainSection, isOnOrderDetailsPath, isOnTripDetailsPath, userId } = getCurrentAdminPath()

    let pageContent: React.ReactNode | null = null
    let header: AdminPageContentHeaderText | null = null
    let hasSearch = false
    let searchQuery = ''
    let searchPlaceholder = ''

    // The user a list row was opened for, but only on that user's own pages: it is never cleared, so
    // after opening one person's trip, another person opened from the Users list would carry their name
    const user =
      (selectedUser?.id.toString() === userId ? selectedUser : undefined) ??
      usersSuccess?.data.records.find((user) => user.id.toString() === userId)

    const emailLink = (
      <a href={`mailto:${user?.email}`} style={linkStyles}>
        {user?.email}
      </a>
    )

    const phoneNumber = user?.phoneNumber ? (
      <a href={`tel:${user?.phoneNumber}`} style={linkStyles}>
        {user?.phoneNumber}
      </a>
    ) : null

    header = {
      title: `${user?.firstName} ${user?.lastName}`,
      subtitle: joinElementsWithBullet([user?.id.toString(), emailLink, phoneNumber])
    }

    if (isOnOrderDetailsPath) {
      const offerRequestId = getOfferRequestId()
      pageContent = <OrderDetails offerRequestId={offerRequestId} focusedItemRequestId={getFocusedItemRequestId()} />
    } else if (isOnTripDetailsPath) {
      pageContent = <TripDetails />
    } else {
      switch (mainSection) {
        case adminNavigationPaths.users:
          hasSearch = false
          searchQuery = isOnOrderDetailsPath ? orderDetailsSearch : tripDetailsSearch
          pageContent = (
            <UserDetails
              userId={userId}
              searchQuery={searchQuery}
              demoRouter={demoRouter}
              setUserDetailsTabIndex={setUserDetailsTabIndex}
              userDetailsTabIndex={userDetailsTabIndex}
            />
          )
          break

        case adminNavigationPaths.offerRequests:
          hasSearch = true
          searchQuery = offerRequestsSearch
          searchPlaceholder = 'Search orders...'
          header = {
            title: 'Offer Requests',
            subtitle: 'Manage offer requests'
          }
          pageContent = (
            <OfferRequests searchQuery={searchQuery} demoRouter={demoRouter} setSelectedUser={setSelectedUser} />
          )
          break

        case adminNavigationPaths.trips:
          hasSearch = true
          searchQuery = tripsSearch
          searchPlaceholder = 'Search trips...'
          header = {
            title: 'Trips',
            subtitle: 'Manage travel itineraries'
          }
          pageContent = <Trips searchQuery={searchQuery} demoRouter={demoRouter} setSelectedUser={setSelectedUser} />
          break
      }
    }

    return { pageContent, header, hasSearch, searchQuery, searchPlaceholder }
  }

  function getPageContent({ pathname }: { pathname: string }) {
    const { mainSection, isOnUserDetailsPath } = getCurrentAdminPath()

    const userDetailsPageContent = getUserDetailsPageContent()
    let pageContent: React.ReactNode | null = null
    let header: AdminPageContentHeaderText | null = null
    let searchPlaceholder: string | undefined = undefined
    let hasSearch = false
    let searchQuery = ''

    switch (mainSection) {
      case adminNavigationPaths.users:
        header = {
          title: 'Users',
          subtitle: 'Manage platform users'
        }

        if (isOnUserDetailsPath) {
          pageContent = userDetailsPageContent.pageContent
          header = userDetailsPageContent.header
          hasSearch = userDetailsPageContent.hasSearch
          searchQuery = userDetailsPageContent.searchQuery
          searchPlaceholder = userDetailsTabIndex === 0 ? 'Search orders...' : 'Search trips...'
        } else {
          hasSearch = true
          searchQuery = userSearch
          searchPlaceholder = 'Search users...'
          pageContent = <Users searchQuery={searchQuery} demoRouter={demoRouter} />
        }
        break

      case adminNavigationPaths.externalBookings:
        hasSearch = false
        searchQuery = externalBookingsSearch
        pageContent = (
          <ExternalBookings
            searchQuery={searchQuery}
            setExternalBookingsTabIndex={setExternalBookingsTabIndex}
            externalBookingsTabIndex={externalBookingsTabIndex}
          />
        )
        searchPlaceholder =
          externalBookingsTabIndex === 0 ? 'Search pending requests...' : 'Search processed requests...'
        header = {
          title: 'External Bookings',
          subtitle: 'Manage external flight booking requests'
        }
        break

      case adminNavigationPaths.discountCodes:
        hasSearch = true
        searchQuery = discountCodesSearch
        pageContent = (
          <DiscountCodes
            searchQuery={searchQuery}
            setDiscountCodesTabIndex={setDiscountCodesTabIndex}
            discountCodesTabIndex={discountCodesTabIndex}
          />
        )
        searchPlaceholder = discountCodesTabIndex === 0 ? 'Search active codes...' : 'Search expired codes...'
        header = {
          title: 'Discount Codes',
          subtitle: 'Manage discount codes for shoppers'
        }
        break

      case adminNavigationPaths.offerRequests:
        pageContent = userDetailsPageContent.pageContent
        header = userDetailsPageContent.header
        hasSearch = userDetailsPageContent.hasSearch
        searchQuery = userDetailsPageContent.searchQuery
        searchPlaceholder = userDetailsPageContent.searchPlaceholder
        break

      case adminNavigationPaths.readyToPurchase:
        hasSearch = true
        searchQuery = readyToPurchaseSearch
        pageContent = (
          <ReadyToPurchase
            setReadyToPurchaseTabIndex={setReadyToPurchaseTabIndex}
            readyToPurchaseTabIndex={readyToPurchaseTabIndex}
            searchQuery={searchQuery}
          />
        )
        searchPlaceholder =
          readyToPurchaseTabIndex === 0
            ? 'Search ready to purchase items...'
            : readyToPurchaseTabIndex === 1
              ? 'Search purchased items...'
              : 'Search delivered items...'
        header = {
          title: 'Ready to Purchase',
          subtitle: 'Manage items ready for purchase'
        }
        break

      case adminNavigationPaths.trips:
        pageContent = userDetailsPageContent.pageContent
        header = userDetailsPageContent.header
        hasSearch = userDetailsPageContent.hasSearch
        searchQuery = userDetailsPageContent.searchQuery
        searchPlaceholder = userDetailsPageContent.searchPlaceholder
        break

      case adminNavigationPaths.fulfillmentOrders: {
        const { isOnFulfillmentOrderDetailsPath, fulfillmentOrderId } = getCurrentAdminPath()

        if (isOnFulfillmentOrderDetailsPath && fulfillmentOrderId) {
          hasSearch = false
          const fulfillmentOrder = fulfillmentOrderDetailsSuccess?.data
          const sender = fulfillmentOrder?.sender
          const senderEmailLink =
            sender && sender?.email ? (
              <a href={`mailto:${sender.email}`} style={linkStyles}>
                {sender.email}
              </a>
            ) : null
          header = {
            title: sender ? `${sender.firstName} ${sender.lastName}` : `Order ${stripPrefixFromId(fulfillmentOrderId)}`,
            subtitle: joinElementsWithBullet([sender?.id ?? null, senderEmailLink])
          }
          pageContent = <FulfillmentOrderDetails orderId={fulfillmentOrderId} />
        } else {
          hasSearch = true
          searchQuery = fulfillmentOrdersSearch
          searchPlaceholder = 'Search fulfillment orders...'
          header = {
            title: 'Fulfillment Orders',
            subtitle: 'Manage fulfillment orders'
          }
          pageContent = <FulfillmentOrders searchQuery={searchQuery} demoRouter={demoRouter} />
        }
        break
      }

      case adminNavigationPaths.lockers:
        hasSearch = false
        header = {
          title: 'Lockers',
          subtitle: 'Manage smart lockers'
        }
        pageContent = <Lockers />
        break

      case adminNavigationPaths.lockerOrders:
        hasSearch = true
        searchQuery = lockerOrdersSearch
        searchPlaceholder = 'Search order #, trip, name or locker order id...'
        header = {
          title: 'Locker Orders',
          subtitle: 'Past locker drop-offs and collections'
        }
        pageContent = (
          <LockerOrders searchQuery={searchQuery} demoRouter={demoRouter} setSelectedUser={setSelectedUser} />
        )
        break
    }

    return (
      <PageContainer
        slots={{
          header: () => (
            <SearchHeader
              navigate={demoRouter.navigate}
              breadcrumbs={getBreadcrumbConfig(pathname)}
              header={{
                title: header?.title || '',
                subtitle: header?.subtitle || ''
              }}
              search={
                hasSearch
                  ? {
                      initialQuery: searchQuery,
                      onSearchSubmit: handleSearchSubmit,
                      onClear: handleClearSearch,
                      placeholder: searchPlaceholder
                    }
                  : undefined
              }
            />
          )
        }}
      >
        {pageContent}
      </PageContainer>
    )
  }

  function getCustomAppTitle() {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <img src={images.hourrierLogo} alt="Hurrier Logo" width={32} height={32} />
        <Typography variant="h6">{env.PRODUCT_NAME} Admin Dashboard</Typography>
        {env.NODE_ENV !== 'production' && <Chip size="small" label={env.NODE_ENV.toUpperCase()} color="info" />}
      </Stack>
    )
  }

  const handleLogout = async () => {
    const onSuccess = () => {
      navigate(routes.login.path, { replace: true })
    }

    await dispatch(logout({ onSuccess }))
  }

  return (
    <AppProvider
      navigation={adminNavigation}
      router={demoRouter}
      branding={{
        title: 'Admin Dashboard'
      }}
      session={{
        user: {
          name: 'Dashboard Admin'
        }
      }}
      authentication={{
        signIn: () => Promise.resolve(),
        signOut: handleLogout
      }}
      theme={theme}
    >
      <DashboardLayout
        sx={styles.dashboardLayout}
        slots={{
          appTitle: getCustomAppTitle
        }}
      >
        {getPageContent({ pathname: demoRouter.pathname })}
      </DashboardLayout>
    </AppProvider>
  )
}

export default AdminDashboard
